import { Component, computed, OnDestroy, OnInit, Signal, signal, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { CustomerModel } from '@libs/account/models';
import { AlertComponent, ButtonComponent, PageHeaderComponent } from '@libs/common/components';
import { AuthService } from '@libs/shared/auth';
import { DialogService, EnvironmentService, ErrorHandlerService, FlashMessageService } from '@libs/shared/common';
import { FOOTER_DPG_CONDITIONS_ROUTE_PATH, FOOTER_GENERAL_TERMS_AND_CONDITIONS_ROUTE_PATH } from '@libs/shared/models';
import {
  CapabilityService,
  CartPollingService,
  CartService,
  ScrollService,
  SHOP_UNAVAILABLE_PRODUCTS_ERROR_CODES,
  ShopErrorParserService,
  ViewportService,
} from '@libs/shared/services';
import { CartTrackingService } from '@libs/shared/tracking';
import { LinkDirective } from '@libs/shared/ui';
import {
  CartCalculationSummaryModel,
  CartItemModel,
  CartModel,
  OrderCheckoutSuccessRouteDataModel,
  OrderTokenModel,
  ShopErrorItemModel,
} from '@libs/shop/models';
import { StatusCodes } from 'http-status-codes';
import { DateTime } from 'luxon';
import { firstValueFrom, interval, Subscription } from 'rxjs';
import { DeliveryWindowsDialogComponent } from '../../delivery-windows-form/delivery-windows-dialog';
import { CheckoutService } from '../../services';
import { OrderBottomBarComponent } from '../order-bottom-bar/order-bottom-bar.component';
import { OrderItemComponent } from '../order-item/order-item.component';
import { OrderOverviewDetailsComponent } from '../order-overview/order-overview-details/order-overview-details.component';
import { OrderOverviewComponent } from '../order-overview/order-overview.component';
import { OrderReferenceTextComponent } from '../order-reference-text/order-reference-text.component';
import { OrderRequestedDeliveryDateComponent } from '../order-requested-delivery-date/order-requested-delivery-date.component';
import {
  OrderUnavailableProductsDialogFormComponent,
  OrderUnavailableProductsDialogFormData,
} from '../order-unavailable-products/order-unavailable-products-dialog-form';
import { OrderUnavailableProductsComponent } from '../order-unavailable-products/order-unavailable-products.component';
import { CheckoutDetailsComponent } from './checkout-details';

@Component({
  selector: 'ui-checkout',
  imports: [
    ButtonComponent,
    CheckoutDetailsComponent,
    LinkDirective,
    OrderOverviewComponent,
    OrderItemComponent,
    AlertComponent,
    DeliveryWindowsDialogComponent,
    OrderReferenceTextComponent,
    OrderRequestedDeliveryDateComponent,
    PageHeaderComponent,
    OrderBottomBarComponent,
    OrderOverviewDetailsComponent,
    OrderUnavailableProductsComponent,
    MatCard,
    MatCardContent,
  ],
  templateUrl: './checkout.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './checkout.component.scss',
})
export class CheckoutComponent implements OnInit, OnDestroy {
  public customer: Signal<CustomerModel | undefined> = this.authService.currentCustomer;
  public cart: WritableSignal<CartModel | undefined> = signal(undefined);
  public deliveryWindowsRefDate: Signal<DateTime> = signal(DateTime.now());
  public isCurrentCustomerBlocked: Signal<boolean> = this.authService.isCurrentCustomerBlocked;
  // Keep links absolute on native platforms and remove trailing slashes before adding paths.
  public appUrl: Signal<string> = computed(() => this.environmentService.links().appUrl.replace(/\/+$/, ''));
  public agbLink: Signal<string> = computed(() => `${this.appUrl()}/${FOOTER_GENERAL_TERMS_AND_CONDITIONS_ROUTE_PATH}`);
  public dpgLink: Signal<string> = computed(() => `${this.appUrl()}/${FOOTER_DPG_CONDITIONS_ROUTE_PATH}`);

  public calculationSummary: Signal<CartCalculationSummaryModel | undefined> = computed(() => this.cart()?.totals);
  public items: Signal<CartItemModel[]> = computed(() => this.cart()?.items ?? []);

  public orderToken: WritableSignal<OrderTokenModel | undefined> = signal(undefined);

  public errors: WritableSignal<ShopErrorItemModel[]> = signal([]);
  public visibleErrors: Signal<ShopErrorItemModel[]> = computed(() =>
    this.errors().filter(x => !SHOP_UNAVAILABLE_PRODUCTS_ERROR_CODES.includes(x.internalErrorCode ?? '')));

  public unavailableProductsErrors: Signal<ShopErrorItemModel[]> = computed(() => this.errors()
    .filter(x => SHOP_UNAVAILABLE_PRODUCTS_ERROR_CODES.includes(x.internalErrorCode ?? '') && Boolean(x.itemPointer)));

  public loading: WritableSignal<boolean> = signal(false);

  public orderButtonDisabled: Signal<boolean> = computed(() => {
    if (this.loading()) {
      return true;
    }

    if (this.isCurrentCustomerBlocked()) {
      return true;
    }

    if (this.unavailableProductsErrors().length && !this.visibleErrors().length) {
      return false;
    }

    if (!this.orderToken()) {
      return true;
    }

    return false;
  });

  public requestedDeliveryDate: WritableSignal<DateTime | undefined> = signal(undefined);
  public requestedDeliveryDateReference: DateTime = DateTime.now();
  public orderCustomReferenceText: WritableSignal<string> = signal('');

  public referenceTextEnabled: Signal<boolean> = this.capabilityService.isAvailable('shop.referenceText');
  public requestedDeliveryDateEnabled: Signal<boolean> = this.capabilityService.isAvailable('shop.requestedDeliveryDate');

  public readonly isMobile: Signal<boolean>;
  public orderBottomBarVisible: Signal<boolean> = computed(() => this.isMobile() && this.capabilityService.isAvailable('shop.newCheckoutUi')());

  private orderTokenRefreshSubscription: Subscription = new Subscription();

  private readonly quoteHashValidationErrorMessage: string = 'quote data hash validation failed.';

  public constructor(
    private readonly dialog: DialogService,
    private readonly cartService: CartService,
    private readonly checkoutService: CheckoutService,
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly viewportService: ViewportService,
    private readonly messageService: FlashMessageService,
    private readonly scrollService: ScrollService,
    private readonly cartPollingService: CartPollingService,
    private readonly flashMessageService: FlashMessageService,
    private readonly capabilityService: CapabilityService,
    private readonly shopErrorParser: ShopErrorParserService,
    private readonly errorHandler: ErrorHandlerService,
    private readonly cartTrackingService: CartTrackingService,
    private readonly environmentService: EnvironmentService,
  ) {
    this.cartService.getCart().pipe(takeUntilDestroyed()).subscribe((cart) => {
      this.cart.set(cart);
      void this.getOrderToken();
    });
    this.isMobile = toSignal(this.viewportService.isMobile(), { initialValue: false });
  }

  public ngOnInit(): void {
    this.cartPollingService.dispose();

    if (!this.isCurrentCustomerBlocked()) {
      this.initOrderTokenPolling();
    }

    this.cartTrackingService.trackCheckoutStep(this.cart(), 'checkout');
  }

  public ngOnDestroy(): void {
    this.orderTokenRefreshSubscription.unsubscribe();
    this.cartPollingService.init();
  }

  public async placeOrder(): Promise<void> {
    if (this.unavailableProductsErrors().length && !this.visibleErrors().length) {
      const shouldDeleteAndOrder: boolean = await this.openUnavailableProductsDialog();

      if (!shouldDeleteAndOrder) {
        return;
      }

      await this.deleteProductsFromCart(this.unavailableProductsErrors().map(x => x.itemPointer as string));
    }

    const cart: CartModel | undefined = this.cart();
    const orderToken: string | undefined = this.orderToken()?.token;

    if (!cart || !orderToken) {
      throw new Error('Cart and or OrderToken are undefined');
    }

    try {
      this.orderTokenRefreshSubscription.unsubscribe();
      this.loading.set(true);

      await firstValueFrom(this.checkoutService.placeOrder(cart.id, orderToken, this.requestedDeliveryDate(), this.orderCustomReferenceText() || undefined));

      this.cartTrackingService.trackCheckout(cart);

      await this.router.navigate([
        'checkout-success',
      ], {
        state: {
          deliveryDate: this.requestedDeliveryDate()?.toISODate() ?? undefined,
          orderCustomReference: this.orderCustomReferenceText(),
        } satisfies OrderCheckoutSuccessRouteDataModel,
      });

      this.scrollService.contentScrollToTop();
    } catch (error: unknown) {
      console.error(error);
      const shopErrors: ShopErrorItemModel[] = this.shopErrorParser.tryParseErrors(error);
      const knownShopErrors: ShopErrorItemModel[] = this.shopErrorParser.tryParseKnownErrors(error);

      if (knownShopErrors.length) {
        this.messageService.showMessage('warningCheckoutValidation');
        this.errors.set(knownShopErrors);

        return;
      }

      if (shopErrors.some(x => x.status === Number(StatusCodes.BAD_REQUEST)
        || x.message.toLowerCase() === this.quoteHashValidationErrorMessage.toLowerCase())) {

        this.messageService.showMessage('infoCheckoutCartChanged');
        await this.cartPollingService.refreshCart();
        await this.getOrderToken();
        this.initOrderTokenPolling();
      } else if (shopErrors.some(x => x.status === Number(StatusCodes.CONFLICT))) {
        this.messageService.showMessage('errorCheckoutOrderAlreadyPlacedForThisCart');
      } else {
        this.initOrderTokenPolling();
        this.messageService.showMessage('errorCheckout');
        this.errorHandler.handleError(error);
        this.errors.set(this.shopErrorParser.tryParseErrors(error));
      }
    } finally {
      this.loading.set(false);
    }
  }

  public onDeliveryDateSelected(date: DateTime | undefined): void {
    this.requestedDeliveryDate.set(date);
  }

  public onOrderCustomReferenceTextChanged(text: string): void {
    this.orderCustomReferenceText.set(text);
  }

  public deleteProductsFromCart(skus: string[]): Promise<void> {
    this.loading.set(true);

    return firstValueFrom(this.cartService.deleteItemsCollection(skus))
      .then(() => {
        this.errors.set([]);

        return this.getOrderToken();
      })
      .finally(() => void this.loading.set(false));
  }

  private async getOrderToken(): Promise<void> {
    if (this.isCurrentCustomerBlocked() || this.unavailableProductsErrors().length) {
      return;
    }

    const cartId: string | undefined = this.cart()?.id;

    if (!cartId) {
      return;
    }

    const orderToken: OrderTokenModel | undefined = this.orderToken();

    if (orderToken && DateTime.fromISO(orderToken.tokenExpirationTimeStamp) > DateTime.now()) {
      return;
    }

    this.orderToken.set(undefined);
    this.loading.set(true);

    try {
      const orderTokenResponse: OrderTokenModel = await firstValueFrom(this.checkoutService.getOrderToken(cartId));

      this.orderToken.set(orderTokenResponse);
    } catch (error: unknown) {
      this.flashMessageService.showMessage('errorCheckoutCartValidationFailed');
      this.errors.set(this.shopErrorParser.tryParseErrors(error));
    } finally {
      this.loading.set(false);
    }
  }

  private async openUnavailableProductsDialog(): Promise<boolean> {
    const data: OrderUnavailableProductsDialogFormData = {
      unavailableProductsErrors: this.unavailableProductsErrors(),
    };

    const isMobile: boolean = this.isMobile();

    const dialogRef: MatDialogRef<OrderUnavailableProductsDialogFormComponent, boolean> = this.dialog.open<
      OrderUnavailableProductsDialogFormComponent,
      OrderUnavailableProductsDialogFormData,
      boolean
    >(OrderUnavailableProductsDialogFormComponent, {
      panelClass: isMobile ? ['flex-dialog-container', 'fullscreen-dialog'] : 'flex-dialog-container',
      restoreFocus: false,
      data,
      width: isMobile ? '100vw' : '40vw',
      maxWidth: isMobile ? '100vw' : '40vw',
      height: isMobile ? '100vh' : undefined,
      maxHeight: isMobile ? '100vh' : undefined,
    });

    return await firstValueFrom(dialogRef.afterClosed()) === true;
  }

  private initOrderTokenPolling(): void {
    this.orderTokenRefreshSubscription.unsubscribe();
    this.orderTokenRefreshSubscription = new Subscription();

    this.orderTokenRefreshSubscription.add(interval(60000).subscribe(() => {
      void this.getOrderToken();
    }));
  }
}
